import { FC, useCallback, useRef } from 'react';
import { graphql, useFragment, usePaginationFragment } from 'react-relay';

import { youMayAlsoLike } from '../../shared/sharedMessages';
import {
    PdpRespProductTileCarousel,
    PdpRespProductTileCarouselProps,
} from './PdpRespProductTileCarousel/PdpRespProductTileCarousel';

// Helpers

import {
    SIMILAR_ITEMS_LIST,
    trackSimilarItemClick,
    trackCarouselItemImpressions,
} from '../tracking/similarItemsTracking';
import { flattenConnectionNodes } from '../../utils/relayUtils';
import { usePaginationHooks } from './util';

import { PdpRespYMALItemsCarousel_viewer$key } from './__generated__/PdpRespYMALItemsCarousel_viewer.graphql';
import { PdpRespYMALItemsCarousel_user$key } from './__generated__/PdpRespYMALItemsCarousel_user.graphql';
import { PdpRespYMALItemsCarousel_item$key } from './__generated__/PdpRespYMALItemsCarousel_item.graphql';
import { locations } from '../../utils/tracking/shared/favoritesTracking';

type Props = {
    viewer: PdpRespYMALItemsCarousel_viewer$key;
    item: PdpRespYMALItemsCarousel_item$key;
    user?: PdpRespYMALItemsCarousel_user$key | null;
    currency: string;
};

const viewerFragment = graphql`
    fragment PdpRespYMALItemsCarousel_viewer on Viewer
    @refetchable(queryName: "PdpRespYMALItemsCarouselRefetchQuery")
    @argumentDefinitions(count: { type: "Int", defaultValue: 6 }, cursor: { type: "String" }) {
        ymalItems: moreLikeThisItem(
            first: $count
            after: $cursor
            seller: EXCLUDE
            itemId: $itemId
            excludeAuctionItems: true
        ) @connection(key: "PdpRespYMALItemsCarousel_ymalItems", filters: []) {
            edges {
                node {
                    serviceId
                    ...PdpRespProductTileCarousel_items
                        @arguments(userCountryCode: $userCountryCode)
                }
            }
        }
        ...PdpRespProductTileCarousel_viewer
    }
`;

const itemFragment = graphql`
    fragment PdpRespYMALItemsCarousel_item on Item {
        serviceId
        isSold
        isUnpurchasable
        seller {
            serviceId
            isAccessible
        }
    }
`;

const userFragment = graphql`
    fragment PdpRespYMALItemsCarousel_user on User {
        ...PdpRespProductTileCarousel_user
    }
`;

export const PdpRespYMALItemsCarousel: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    user: userRef,
    currency,
}) => {
    const { data: viewer, loadNext } = usePaginationFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef || null);
    const impressionTrackingRef = useRef({});
    const { isSold, isUnpurchasable } = item;
    const isUnavailablePdp = !!(isSold || isUnpurchasable);
    const trackingLocation = isUnavailablePdp ? 'sold pdp you may also like' : SIMILAR_ITEMS_LIST;

    const { onClickArrow, onCarouselReset } = usePaginationHooks(loadNext, {
        label: SIMILAR_ITEMS_LIST,
    });
    const relatedItems = flattenConnectionNodes(viewer.ymalItems);

    const onItemsVisible = useCallback<
        NonNullable<PdpRespProductTileCarouselProps['onItemsVisible']>
    >(
        (visibleItems, startIndex) =>
            trackCarouselItemImpressions({
                visibleItems,
                startIndex,
                impressionTrackingRef,
                list: SIMILAR_ITEMS_LIST,
            }),
        []
    );

    const onClickItemTile = useCallback<
        NonNullable<PdpRespProductTileCarouselProps['onClickItemTile']>
    >(
        (tileItem, index) => {
            trackSimilarItemClick(index, tileItem, trackingLocation);
        },
        [trackingLocation]
    );

    if (!relatedItems.length) {
        return null;
    }

    return (
        <PdpRespProductTileCarousel
            viewer={viewer}
            items={relatedItems}
            user={user}
            currency={currency}
            title={youMayAlsoLike}
            location={locations.SIMILAR_ITEM_GRID}
            onClickItemTile={onClickItemTile}
            onClickArrow={onClickArrow}
            onItemsVisible={onItemsVisible}
            onCarouselReset={onCarouselReset}
        />
    );
};
